
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useCreateClient, useUpdateClient } from "@/hooks/clients";
import { useUpdateClientPaymentStatus } from "@/hooks/clients/useUpdateClientPaymentStatus";
import { useWorkoutTypes } from "@/hooks/useWorkoutTypes";
import { createFormSchema, FormData, getDefaultFormValues } from "@/components/clients/utils/clientFormSchema";
import { UseClientFormManagerProps, ClientFormManagerReturn } from "./types";
import { getDefaultSubscriptions } from "./formDataUtils";
import { validateCurrentStep, validateAndNavigateToFirstError } from "./formValidation";
import { initializeForm } from "./formInitialization";
import { createFormHandlers } from "./formHandlers";

export const useClientFormManager = ({ open, onOpenChange, initialData, organizationId }: UseClientFormManagerProps): ClientFormManagerReturn => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isEditMode = !!initialData;
  const [activeTab, setActiveTab] = useState("details");
  const { workoutTypes = [] } = useWorkoutTypes();
  const createClientMutation = useCreateClient();
  const updateClientMutation = useUpdateClient();
  const updatePaymentStatusMutation = useUpdateClientPaymentStatus();

  const formSchema = createFormSchema(t);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...getDefaultFormValues(),
      subscriptions: getDefaultSubscriptions(workoutTypes)
    },
  });

  // Reset form when dialog opens/closes or when workout types change
  useEffect(() => {
    initializeForm(form, open, workoutTypes, initialData, setActiveTab);
  }, [open, workoutTypes, initialData, form]);

  const handleNextStep = async () => {
    // First validate the current step fields specifically
    const isCurrentStepValid = await validateCurrentStep(form, activeTab);
    
    if (isCurrentStepValid) {
      setActiveTab("subscriptions");
    } else {
      // If current step is invalid, use the validation function to navigate to errors
      await validateAndNavigateToFirstError(form, setActiveTab);
    }
  };

  const handleBackStep = () => {
    setActiveTab("details");
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  const handleSuccess = () => {
    onOpenChange(false);
    form.reset();
    setActiveTab("details");
  };

  const navigateToClient = (clientId: string) => {
    navigate(`/client/${clientId}`);
  };

  const { handleDetailsSubmit, handleSubscriptionsSubmit, handleFullSubmit } = createFormHandlers({
    isEditMode,
    initialData,
    createClientMutation,
    updateClientMutation,
    updatePaymentStatusMutation,
    handleSuccess,
    setActiveTab,
    form,
    navigateToClient,
    organizationId
  });

  const isLoading = createClientMutation.isPending || updateClientMutation.isPending || updatePaymentStatusMutation.isPending;

  // Prevent form submission if no workout types exist
  const hasWorkoutTypes = workoutTypes.length > 0;

  return {
    form,
    activeTab,
    setActiveTab,
    workoutTypes,
    isEditMode,
    isLoading,
    hasWorkoutTypes,
    handleNextStep,
    handleBackStep,
    handleCancel,
    handleDetailsSubmit,
    handleSubscriptionsSubmit,
    handleFullSubmit
  };
};
